// Dependency-free QR Code encoder (ISO/IEC 18004): UTF-8 byte mode, EC levels L/M/Q/H, versions 1..10.
// qrMatrix(text, { ecc, mask }) -> boolean[][] (true = dark, no quiet zone); qrSvg(text, opts) -> SVG string.
// `mask` (0..7) is a test-only override; by default the mask with the lowest penalty score is used.

const ECC_BITS = { L: 1, M: 0, Q: 3, H: 2 }
const EC_PER_BLOCK = {
  L: [7, 10, 15, 20, 26, 18, 20, 24, 30, 18],
  M: [10, 16, 26, 18, 24, 16, 18, 22, 22, 26],
  Q: [13, 22, 18, 26, 18, 24, 18, 22, 20, 24],
  H: [17, 28, 22, 16, 22, 28, 26, 26, 24, 28],
}
const NUM_BLOCKS = {
  L: [1, 1, 1, 1, 1, 2, 2, 2, 2, 4],
  M: [1, 1, 1, 2, 2, 4, 4, 4, 5, 5],
  Q: [1, 1, 2, 2, 4, 4, 6, 6, 8, 8],
  H: [1, 1, 2, 4, 4, 4, 5, 6, 8, 8],
}
const ALIGN = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]]
export const MAX_VERSION = 10

// ---- GF(256), polynomial 0x11D ----
const EXP = new Array(512), LOG = new Array(256)
for (let i = 0, x = 1; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d }
for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]
const gmul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]])

function rsGenerator(deg) {
  let g = [1]
  for (let i = 0; i < deg; i++) {
    const next = new Array(g.length + 1).fill(0)
    for (let j = 0; j < g.length; j++) { next[j] ^= g[j]; next[j + 1] ^= gmul(g[j], EXP[i]) }
    g = next
  }
  return g // highest degree first, leading 1
}
function rsRemainder(data, deg) {
  const g = rsGenerator(deg), rem = new Array(deg).fill(0)
  for (const b of data) {
    const f = b ^ rem.shift(); rem.push(0)
    if (f) for (let i = 0; i < deg; i++) rem[i] ^= gmul(g[i + 1], f)
  }
  return rem
}

function rawModules(v) {
  let n = (16 * v + 128) * v + 64
  if (v >= 2) { const na = Math.floor(v / 7) + 2; n -= (25 * na - 10) * na - 55 }
  if (v >= 7) n -= 36
  return n
}
function dataCodewords(v, ecc) {
  return Math.floor(rawModules(v) / 8) - EC_PER_BLOCK[ecc][v - 1] * NUM_BLOCKS[ecc][v - 1]
}

function utf8(text) {
  if (typeof TextEncoder !== 'undefined') return Array.from(new TextEncoder().encode(text))
  const out = []
  for (const ch of String(text)) {
    const c = ch.codePointAt(0)
    if (c < 0x80) out.push(c)
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63))
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63))
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63))
  }
  return out
}

function buildCodewords(bytes, v, ecc) {
  const cap = dataCodewords(v, ecc), bits = []
  const put = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1) }
  put(4, 4); put(bytes.length, v < 10 ? 8 : 16)
  for (const b of bytes) put(b, 8)
  put(0, Math.min(4, cap * 8 - bits.length))
  while (bits.length % 8) bits.push(0)
  const data = []
  for (let i = 0; i < bits.length; i += 8) { let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]; data.push(b) }
  for (let pad = 0xec; data.length < cap; pad ^= 0xec ^ 0x11) data.push(pad)

  // split into blocks, add RS, interleave
  const nb = NUM_BLOCKS[ecc][v - 1], ecLen = EC_PER_BLOCK[ecc][v - 1], total = Math.floor(rawModules(v) / 8)
  const nShort = nb - (total % nb), shortLen = Math.floor(total / nb)
  const blocks = []
  for (let i = 0, k = 0; i < nb; i++) {
    const dl = shortLen - ecLen + (i < nShort ? 0 : 1)
    const d = data.slice(k, k + dl); k += dl
    blocks.push({ d, e: rsRemainder(d, ecLen) })
  }
  const out = []
  for (let i = 0; i <= shortLen - ecLen; i++) for (const b of blocks) if (i < b.d.length) out.push(b.d[i])
  for (let i = 0; i < ecLen; i++) for (const b of blocks) out.push(b.e[i])
  return out
}

const MASKS = [
  (x, y) => (x + y) % 2 === 0,
  (x, y) => y % 2 === 0,
  (x, y) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
]

function penalty(m) {
  const n = m.length
  let score = 0, dark = 0
  const line = get => {
    let run = 1
    for (let i = 1; i < n; i++) {
      if (get(i) === get(i - 1)) { run++; if (run === 5) score += 3; else if (run > 5) score++ } else run = 1
    }
    // finder-like 1011101 with 4 light on either side (outside the symbol counts as light)
    const at = i => (i >= 0 && i < n ? get(i) : false)
    for (let i = -4; i + 6 < n + 4; i++) {
      if (at(i) && !at(i + 1) && at(i + 2) && at(i + 3) && at(i + 4) && !at(i + 5) && at(i + 6)) {
        let before = true, after = true
        for (let k = 1; k <= 4; k++) { if (at(i - k)) before = false; if (at(i + 6 + k)) after = false }
        if (before) score += 40
        if (after) score += 40
      }
    }
  }
  for (let y = 0; y < n; y++) line(i => m[y][i])
  for (let x = 0; x < n; x++) line(i => m[i][x])
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    if (m[y][x]) dark++
    if (x + 1 < n && y + 1 < n && m[y][x] === m[y][x + 1] && m[y][x] === m[y + 1][x] && m[y][x] === m[y + 1][x + 1]) score += 3
  }
  score += 10 * Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5)
  return score
}

export function qrMatrix(text, { ecc = 'M', mask } = {}) {
  if (!(ecc in ECC_BITS)) throw new Error('qr: ecc must be one of L, M, Q, H')
  if (mask != null && !(Number.isInteger(mask) && mask >= 0 && mask <= 7)) throw new Error('qr: mask must be 0..7')
  const bytes = utf8(text == null ? '' : String(text))
  let v = 1
  const need = ver => 4 + (ver < 10 ? 8 : 16) + bytes.length * 8
  while (v <= MAX_VERSION && need(v) > dataCodewords(v, ecc) * 8) v++
  if (v > MAX_VERSION) {
    throw new Error(`qr: text too long (${bytes.length} bytes; at most ${Math.floor((dataCodewords(MAX_VERSION, ecc) * 8 - 4 - 16) / 8)} bytes at level ${ecc})`)
  }
  const size = v * 4 + 17
  const mod = Array.from({ length: size }, () => new Array(size).fill(false))
  const fn = Array.from({ length: size }, () => new Array(size).fill(false))
  const set = (x, y, dark) => { if (x >= 0 && y >= 0 && x < size && y < size) { mod[y][x] = !!dark; fn[y][x] = true } }

  for (let i = 0; i < size; i++) { set(6, i, i % 2 === 0); set(i, 6, i % 2 === 0) }
  for (const [cx, cy] of [[3, 3], [size - 4, 3], [3, size - 4]]) {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
      const d = Math.max(Math.abs(dx), Math.abs(dy))
      set(cx + dx, cy + dy, d !== 2 && d !== 4)
    }
  }
  const ap = ALIGN[v - 1]
  for (let i = 0; i < ap.length; i++) for (let j = 0; j < ap.length; j++) {
    if ((i === 0 && j === 0) || (i === 0 && j === ap.length - 1) || (i === ap.length - 1 && j === 0)) continue
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) set(ap[i] + dx, ap[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
  }
  const drawFormat = mk => {
    let data = (ECC_BITS[ecc] << 3) | mk, rem = data
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
    const bits = ((data << 10) | rem) ^ 0x5412
    const b = i => ((bits >>> i) & 1) === 1
    for (let i = 0; i <= 5; i++) set(8, i, b(i))
    set(8, 7, b(6)); set(8, 8, b(7)); set(7, 8, b(8))
    for (let i = 9; i < 15; i++) set(14 - i, 8, b(i))
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, b(i))
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, b(i))
    set(8, size - 8, true) // dark module
  }
  drawFormat(0) // reserve
  if (v >= 7) {
    let rem = v
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
    const bits = (v << 12) | rem
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >>> i) & 1) === 1, a = size - 11 + (i % 3), b = Math.floor(i / 3)
      set(a, b, bit); set(b, a, bit)
    }
  }

  // data placement (zigzag)
  const cw = buildCodewords(bytes, v, ecc)
  let bi = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++) for (let j = 0; j < 2; j++) {
      const x = right - j, up = ((right + 1) & 2) === 0, y = up ? size - 1 - vert : vert
      if (!fn[y][x] && bi < cw.length * 8) { mod[y][x] = ((cw[bi >>> 3] >>> (7 - (bi & 7))) & 1) === 1; bi++ }
    }
  }
  const applyMask = mk => { for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!fn[y][x] && MASKS[mk](x, y)) mod[y][x] = !mod[y][x] }

  let best = mask, bestScore = Infinity
  if (mask == null) {
    for (let mk = 0; mk < 8; mk++) {
      applyMask(mk); drawFormat(mk)
      const s = penalty(mod)
      if (s < bestScore) { bestScore = s; best = mk }
      applyMask(mk)
    }
  }
  applyMask(best); drawFormat(best)
  return mod
}

const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export function qrSvg(text, { size = 256, margin = 4, dark = '#0A1628', light = '#ffffff', ecc = 'M', label = 'QR code' } = {}) {
  const m = qrMatrix(text, { ecc }), n = m.length, dim = n + margin * 2
  let d = ''
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (!m[y][x]) continue
      let w = 1
      while (x + w < n && m[y][x + w]) w++
      d += `M${x + margin} ${y + margin}h${w}v1h-${w}z`
      x += w
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" width="${size}" height="${size}" shape-rendering="crispEdges" role="img" aria-label="${esc(label)}">` +
    `<rect width="${dim}" height="${dim}" fill="${esc(light)}"/><path fill="${esc(dark)}" d="${d}"/></svg>`
}
